import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import shortid from 'shortid';
import api from 'api';
import Util from 'utility';
import UnitConvert from 'UnitConverter';
import Loading from 'components/Loading/index';
import { __ } from 'locale';

import RecipeSearch from 'components/RecipeSearch';
import EvaluationPanelSimple from 'components/EvaluationPanel/EvaluationPanelSimple';

import {
    setData,
    addFoodGroup,
    setFoodGroupDescription,
    addFood,
    addFoodModification,
    deleteEmptyFoodGroups,
    setRecipeTitle,
    showMessage,
} from 'actions/index';

function LectureTaskPanelAddRecipe({ mealId, isDrinkRegime, onRecipeAdded }) {
    const data = useSelector(state => state);
    const dispatch = useDispatch();
    const history = useHistory();

    const [recipeLoading, setRecipeLoading] = useState(false);
    const [currentRecipe, setCurrentRecipe] = useState(false);

    const showRecipeDetail = (id) => {
        setRecipeLoading(true);
        api('recipes/detail/' + id).then(result => {
            setTimeout(() => {
                setRecipeLoading(false);
                setCurrentRecipe(result.data);
            }, 100);
        });
    };

    const closeRecipeDetail = () => {
        setRecipeLoading(false);
        setCurrentRecipe(false);
    };

    const addToRecipe = (recipeId) => {
        api('recipes/add/' + recipeId).then(result => {
            result = result.data;
            dispatch(deleteEmptyFoodGroups({ mealHash: mealId }));

            Object.keys(result.groups).forEach(g => {
                const group = result.groups[g];
                dispatch(addFoodGroup({ mealHash: mealId, id: group.group_id, title: group.title, type: group.type, opened: true }));
                dispatch(setFoodGroupDescription({ mealHash: mealId, id: group.group_id, description: group.description }));
            });

            Object.keys(result.foods).forEach(f => {
                const food = result.foods[f];
                const newFoodId = shortid();
                dispatch(addFood({
                    mealHash: mealId,
                    id: newFoodId,
                    food_id: food.food_id,
                    group_id: food.group_id,
                    is_drink: food.is_drink,
                    weight: food.amount,
                    image: 'https://www.zofapp.cz/data/zof/item/' + food.food_id + '.svg',
                    title: data.globals.itemData[food.food_id]['title'],
                }));
                Object.keys(food.modifications).forEach(m => {
                    dispatch(addFoodModification({ foodId: newFoodId, modificationId: food.modifications[m] }));
                });
            });

            setCurrentRecipe(false);
            setRecipeLoading(false);

            const currentRecipeTitle = data.titles[data.activeMealHash];
            if (currentRecipeTitle === '') {
                dispatch(setRecipeTitle({ mealHash: data.activeMealHash, title: result.title }));
            }

            dispatch(setData({ lastOpenedCookBookRecipe: recipeId, lastAddedRecipeTitle: result.title }));
            dispatch(showMessage({ show: true, type: 'success', message: __('Recept byl vložen') }));

            if (typeof onRecipeAdded === 'function') onRecipeAdded();
        });
    };

    let content;

    if (recipeLoading) {
        content = <Loading />;
    } else if (currentRecipe) {
        const recipe = currentRecipe;
        const ingredients = [];

        for (let i in recipe.ingredients) {
            const ingr = recipe.ingredients[i];

            let undesirableFoodForProfiles = [];
            if (typeof data.appModeData !== 'undefined') {
                if (typeof data.appModeData.undesirableFoods !== 'undefined') {
                    if (data.appModeData.undesirableFoods.includes(ingr.food_id)) {
                        if (typeof data.appModeData.lectureId === 'number') {
                            undesirableFoodForProfiles.push(data.appModeData.profileId);
                        }
                    }
                }
            } else {
                for (let profileId in data.globals.undesirableFoods) {
                    profileId = profileId * 1;
                    if (data.globals.undesirableFoods[profileId].includes(ingr.food_id)) {
                        undesirableFoodForProfiles.push(profileId);
                    }
                }
            }

            const itemRowClass = undesirableFoodForProfiles.length > 0
                ? 'flex items-center gap-[0.6em] py-[0.5em] border-b border-[#f3f3f3] bg-[#fff5f5]'
                : 'flex items-center gap-[0.6em] py-[0.5em] border-b border-[#f3f3f3]';
            const undesirableFoodWarning = (!Util.isNutriKucharMode(data.appMode) && undesirableFoodForProfiles.length > 0)
                ? <p className="text-[#f00] text-[0.9em] m-0">Nevhodná potravina</p>
                : false;

            let ingredientAmount = false;
            if (Util.userHasPurchased('ZOF_COOKBOOK')) {
                const foodItem = {
                    id: ingr['item_id'],
                    selectedWeight: ingr['amount'],
                    weight: data.globals.itemData[ingr['item_id']].weight,
                    norm_unit_amount: data.globals.itemData[ingr['item_id']].norm_unit_amount * 1.0,
                    norm_unit_unit: data.globals.itemData[ingr['item_id']].norm_unit_unit,
                    norm_unit2_amount: data.globals.itemData[ingr['item_id']].norm_unit2_amount * 1.0,
                    norm_unit2_unit: data.globals.itemData[ingr['item_id']].norm_unit2_unit
                };
                const uc = UnitConvert;
                const normalizedUnitInfo = uc.convert(foodItem);
                ingredientAmount = (normalizedUnitInfo !== null && normalizedUnitInfo !== '' && normalizedUnitInfo !== ' null')
                    ? normalizedUnitInfo
                    : false;
            }

            ingredients.push(
                <div className={itemRowClass} key={i}>
                    <img className="w-[32px] h-[32px] shrink-0" src={`https://www.zofapp.cz/data/zof/item/${ingr.food_id}.svg`} alt="" />
                    <div className="flex-1 min-w-0">
                        <p className="m-0 text-[0.9em] font-medium text-[#222]">{ingr['title']}</p>
                        {ingredientAmount && <p className="m-0 text-[0.8em] text-[#888]">{ingredientAmount}</p>}
                        {undesirableFoodWarning}
                    </div>
                </div>
            );
        }

        const recipeLocked = recipe.user_id !== data.user.id && !Util.userHasPurchased('ZOF_COOKBOOK');

        let preparationInfo, addToRecipeButton = null;
        if (!recipeLocked) {
            const description = Object.values(recipe.groups).map((group, idx) =>
                Object.values(recipe.groups).length > 1 ? group.title + '\n\n' + group.description + '\n\n' : group.description
            ).join('\n');
            preparationInfo = (
                <div className="flex items-start">
                    <div>{Util.parseRecipeDescription(description)}</div>
                </div>
            );
            addToRecipeButton = <button className="bg-[#26AA52] text-white py-[0.5em] px-[1em] rounded cursor-pointer border-none hover:bg-[#2dcb62] flex items-center gap-[0.4em]" onClick={() => addToRecipe(recipe.id)}><i className="fa fa-plus"></i> {__('Přidat do receptu')}</button>;
        } else {
            preparationInfo = (
                <>
                    <h3>Příprava receptu</h3>
                    <div>Příprava receptu se zobrazí po zakoupení ZOF kuchařky.</div>
                </>
            );
        }

        const cookbookNotBoughtAlert = recipeLocked ? (
            <div className="rounded mb-[1em] p-[1em] flex items-center bg-[#ffc] text-[#774]">
                <i className="fa fa-info-circle text-[1.5em]"></i>
                <p className="m-0 ml-[1em]">Nemáte přístup k receptům ZOF kuchařky. Pro aktivaci této funkce je nutné <a className="font-bold underline cursor-pointer" onClick={() => history.push('/cookbook/buy')}>kuchařku objednat</a>.</p>
            </div>
        ) : null;

        content = (
            <>
                <div className="flex items-center gap-[0.75em] px-[0.75em] py-[0.6em] border-b border-[#f0f0f0]">
                    <a className="inline-flex items-center gap-[0.4em] text-[0.85em] text-[#555] cursor-pointer hover:text-black shrink-0" onClick={closeRecipeDetail}>
                        <i className="fa fa-angle-left"></i> Zpět
                    </a>
                    <h2 className="m-0 text-[1.05em] font-semibold flex-1">{recipe.lang.title}</h2>
                    <div className="shrink-0">{addToRecipeButton}</div>
                </div>
                <div className="px-[0.75em] py-[0.75em]">
                    {cookbookNotBoughtAlert}
                    <div className="flex mt-[0.5em] gap-[1.5em]">
                        <div className="flex-1 min-w-0">
                            <img className="w-full h-auto block rounded mb-[0.75em]" src={`https://www.zofapp.cz/data/zof/recipe/${Math.floor(recipe.id / 1000)}/${recipe.id}.jpg`} alt="" />
                            <h2 className="text-[0.95em] font-semibold text-[#555] uppercase tracking-wide mb-[0.5em]">{__('Ingredience')} <span className="font-normal normal-case tracking-normal text-[#aaa]">({parseFloat(recipe.nutrition_data.weight)} g)</span></h2>
                            <div className="flex flex-col">{ingredients}</div>
                        </div>
                        <div className="basis-[260px] shrink-0">
                            <h3 className="text-[0.95em] font-semibold text-[#555] uppercase tracking-wide mb-[0.5em]">{__('Postup přípravy')}</h3>
                            {preparationInfo}
                            <h3 className="text-[0.95em] font-semibold text-[#555] uppercase tracking-wide mt-[1em] mb-[0.5em]">{__('Nutriční hodnoty')}</h3>
                            <EvaluationPanelSimple recipe={recipe} opened={true} />
                        </div>
                    </div>
                </div>
            </>
        );
    } else {
        content = <RecipeSearch onClick={showRecipeDetail} isEmbedded={true} isDrinkRegime={isDrinkRegime} />;
    }

    return (
        <div className="flex flex-col flex-1">
            {content}
        </div>
    );
}

export default LectureTaskPanelAddRecipe;
